import React, { useState, createContext, useContext, useCallback, useEffect } from 'react';

export const ProgramContext = createContext();

const SESSION_KEY = 'otp_id';
export const QUIZ_STATE_KEY = 'quiz_state';

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(QUIZ_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const persistState = (state) => {
  try {
    sessionStorage.setItem(QUIZ_STATE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
};

// eslint-disable-next-line react/prop-types
export const ProgramContextProvider = ({ children }) => {
  const saved = loadPersistedState();

  const [selectedDiet, setSelectedDiet] = useState(saved.selectedDiet || null);
  const [selectedProgram, setSelectedProgram] = useState(saved.selectedProgram || null);
  const [programSize, setProgramSize] = useState(saved.programSize || null);
  const [mealBundle, setMealBundle] = useState(saved.mealBundle || null);
  const [daysOfFood, setDaysOfFood] = useState(saved.daysOfFood || null);
  const [bundleId, setBundleId] = useState(saved.bundleId || null);
  const [oneTimeProgramId, setOneTimeProgramIdState] = useState(
    () => sessionStorage.getItem(SESSION_KEY) || null
  );
  const [backgroundJobId, setBackgroundJobId] = useState(null);
  const [totalAmount, setTotalAmount] = useState(saved.totalAmount || null);

  // Quiz - Lifestyle (Phase 2)
  const [gender, setGender] = useState(saved.gender || null);
  const [activity, setActivity] = useState(saved.activity || null);
  const [training, setTraining] = useState(saved.training || null);
  const [glp1, setGlp1] = useState(saved.glp1 || null);
  const [foodRel, setFoodRel] = useState(saved.foodRel || []);
  const [household, setHousehold] = useState(saved.household || null);

  // Quiz - Goals & Taste (Phase 1 & 3)
  const [goals, setGoals] = useState(saved.goals || []);
  const [cuisines, setCuisines] = useState(saved.cuisines || []);
  const [proteins, setProteins] = useState(saved.proteins || []);
  const [avoidances, setAvoidances] = useState(saved.avoidances || []);

  // Quiz - Biomarkers (Phase 4)
  const [bioFlags, setBioFlags] = useState(saved.bioFlags || {});

  // Persist quiz state to sessionStorage on every change
  useEffect(() => {
    persistState({
      selectedDiet,
      selectedProgram,
      programSize,
      mealBundle,
      daysOfFood,
      bundleId,
      totalAmount,
      gender,
      activity,
      training,
      glp1,
      foodRel,
      household,
      goals,
      cuisines,
      proteins,
      avoidances,
      bioFlags,
    });
  }, [
    selectedDiet, selectedProgram, programSize, mealBundle, daysOfFood,
    bundleId, totalAmount, gender, activity, training, glp1, foodRel,
    household, goals, cuisines, proteins, avoidances, bioFlags,
  ]);

  const resetQuizState = useCallback(() => {
    sessionStorage.removeItem(QUIZ_STATE_KEY);
    setSelectedDiet(null);
    setSelectedProgram(null);
    setProgramSize(null);
    setMealBundle(null);
    setDaysOfFood(null);
    setBundleId(null);
    setTotalAmount(null);
    setGender(null);
    setActivity(null);
    setTraining(null);
    setGlp1(null);
    setFoodRel([]);
    setHousehold(null);
    setGoals([]);
    setCuisines([]);
    setProteins([]);
    setAvoidances([]);
    setBioFlags({});
  }, []);

  const setOneTimeProgramId = useCallback((id) => {
    if (id) {
      sessionStorage.setItem(SESSION_KEY, id);
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
    setOneTimeProgramIdState(id);
  }, []);

  // Derived values
  const flagCount = Object.keys(bioFlags).length;

  const toggleFlag = useCallback((marker) => {
    setBioFlags((f) => {
      const cur = f[marker];
      if (!cur) return { ...f, [marker]: 'TOO_HIGH' };
      if (cur === 'TOO_HIGH') return { ...f, [marker]: 'TOO_LOW' };
      const n = { ...f };
      delete n[marker];
      return n;
    });
  }, []);

  const progLabel = selectedProgram === 'sustain'
    ? 'Sustain GLP-1' : selectedProgram === 'peak'
      ? 'Peak' : 'Signature';

  const sizeLabel = programSize?.name || 'Standard';

  const getProgramSpecs = useCallback((prog, sz) => {
    if (prog === 'peak' && sz === 'xl') return { prot: '55g+', cal: '700-900', protDay: '165-195g', calDay: '2100-2700' };
    if (prog === 'peak') return { prot: '45g+', cal: '500-700', protDay: '135-165g', calDay: '1500-1950' };
    if (sz === 'large') return { prot: '45g+', cal: '500-700', protDay: '120-140g', calDay: '1500-1950' };
    return { prot: '35g+', cal: '350-475', protDay: '100-120g', calDay: '1050-1425' };
  }, []);

  const specs = getProgramSpecs(selectedProgram, programSize);

  const bundleLabel = mealBundle?.description || '';
  const daysLabel = daysOfFood?.name ? `${daysOfFood.name}/week` : '';

  // const getRecommendedProgram = useCallback(() => {
  //   if (glp1) return 'sustain';
  //   if ((activity === 'ACTIVE' || activity === 'ATHLETE') &&
  //       (goals.includes('performance') || training === 'STRENGTH_FOCUSED' || foodRel.includes('train'))) return 'peak';
  //   return 'signature';
  // }, [glp1, activity, goals, training, foodRel]);

  const getRecommendedProgram = useCallback(() => {
    if (glp1) return 'sustain';
    if (gender === 'MALE' && (activity === 'MODERATE' || activity === 'ACTIVE' || activity === 'ATHLETE')) return 'peak';
    if (gender === 'FEMALE' && (activity === 'ACTIVE' || activity === 'ATHLETE')) return 'peak';
    return 'signature';
  }, [glp1, gender, activity]);

  const getRecommendedSize = useCallback((prog) => {
    if (prog === 'peak') {
      if (gender === 'MALE') return activity === 'MODERATE' ? 'large' : 'xl';
      return 'large'; // FEMALE
    }
    if (prog === 'sustain') return gender === 'MALE' ? 'large' : 'standard';
    // Signature
    if (gender === 'MALE') return 'large';
    if (gender === 'FEMALE') return 'standard';
    return 'standard';
  }, [gender, activity]);

  const getRecCopy = useCallback((prog) => {
    if (prog === 'sustain') {
      const pd = programSize === 'large' ? '120-140g' : '100-120g';
      return `Since you're on a GLP-1 medication, Sustain GLP-1 is built specifically for you. ${pd} protein per day, portioned for suppressed appetites and formulated to minimize the nausea and bloating GLP-1 users know too well. The medication handles hunger—we make sure you're protecting lean mass and actually getting nutrition in.`;
    }
    if (prog === 'peak') return 'You train hard and you need food that keeps up. Peak gives you 45-65g protein per meal, built around complex carbs for glycogen recovery and leaner fat profiles for faster digestion. Same chef-crafted quality, same ingredient standards—just built for performance.';
    if (foodRel.includes('doctor')) return 'Your doctor said change your diet. Signature makes that actually doable. 350–475 calories and 35g+ protein per meal, every macro dialed in—with every ingredient weighed to the gram. Ships with protein truffles, ACV shrubs, and organic matcha.';
    return 'Signature is our flagship—the program that built our reputation. 35–50g protein per lunch/dinner meal, calorie-controlled, macro-balanced meals designed to transform your body composition, energy, and mood without ever feeling like a diet. Ships with protein truffles, ACV shrubs, and organic matcha.';
  }, [programSize, foodRel]);

  return (
    <ProgramContext.Provider
      value={{
        backgroundJobId,
        setBackgroundJobId,
        bundleId,
        oneTimeProgramId,
        setOneTimeProgramId,
        setBundleId,
        selectedDiet,
        setSelectedDiet,
        selectedProgram,
        setSelectedProgram,
        programSize,
        setProgramSize,
        mealBundle,
        setMealBundle,
        daysOfFood,
        setDaysOfFood,
        totalAmount,
        setTotalAmount,
        // Quiz - Lifestyle
        gender,
        setGender,
        activity,
        setActivity,
        training,
        setTraining,
        glp1,
        setGlp1,
        foodRel,
        setFoodRel,
        household,
        setHousehold,
        // Quiz - Goals & Taste
        goals,
        setGoals,
        cuisines,
        setCuisines,
        proteins,
        setProteins,
        avoidances,
        setAvoidances,
        // Quiz - Biomarkers
        bioFlags,
        setBioFlags,
        // Reset
        resetQuizState,
        // Derived values & helpers
        flagCount,
        toggleFlag,
        progLabel,
        sizeLabel,
        specs,
        bundleLabel,
        daysLabel,
        getProgramSpecs,
        getRecommendedProgram,
        getRecommendedSize,
        getRecCopy,
      }}
    >
      {children}
    </ProgramContext.Provider>
  );
};

export const useProgramContext = () => {
  const context = useContext(ProgramContext);
  if (!context) {
    throw new Error('useProgramContext must be used within a ProgramContextProvider');
  }
  return context;
};
